import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Bot, Pencil, Plus, Shield, Trash2 } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { CopyButton } from "@/components/animate-ui/components/buttons/copy";
import { Blocks } from "@/components/animate-ui/icons/blocks";
import { Check } from "@/components/animate-ui/icons/check";
import { Lightbulb } from "@/components/animate-ui/icons/lightbulb";
import { LockKeyholeOpen } from "@/components/animate-ui/icons/lock-keyhole-open";
import { RefreshCw } from "@/components/animate-ui/icons/refresh-cw";
import { User } from "@/components/animate-ui/icons/user";
import { X } from "@/components/animate-ui/icons/x";
import { CountrySelector } from "@/components/country-selector";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { authClient } from "@/lib/api/auth-client";
import { memoriesFetch } from "@/lib/api/memories";
import { type ToolPermissionPolicy, toolPermissionsFetch } from "@/lib/api/tool-permissions";
import { buildMetaTags } from "@/lib/seo/meta";

export const Route = createFileRoute("/_dashboard/account")({
	head: () => {
		const meta = buildMetaTags({
			title: "Account",
			description: "Manage your account settings",
			noIndex: true,
			noFollow: true,
		});

		return { meta };
	},

	component: AccountPage,
});

// --- Memory types ---

interface MemoryItem {
	id: number;
	content: string;
	source: string;
	source_run_id: number | null;
	created_at: string;
	updated_at: string;
}

// --- Page ---

function AccountPage() {
	return (
		<div className="container mx-auto px-4 py-6 max-w-4xl">
			<div className="mb-8">
				<h1 className="text-3xl font-bold tracking-tight">Account Settings</h1>
			</div>

			<div className="space-y-6">
				<ProfileSection />
				<MemoriesSection />
				<ToolPermissionsSection />
				<SSOStatusSection />
				<ApiKeySection />
			</div>
		</div>
	);
}

function ProfileSection() {
	const emailId = useId();
	const firstNameId = useId();
	const lastNameId = useId();
	const companyId = useId();
	const jobTitleId = useId();
	const queryClient = useQueryClient();
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [company, setCompany] = useState("");
	const [jobTitle, setJobTitle] = useState("");
	const [country, setCountry] = useState("");
	const [hasChanges, setHasChanges] = useState(false);

	// Fetch current user
	const { data: user, isLoading } = useQuery({
		queryKey: ["current-user"],
		queryFn: async () => {
			const response = await authClient.GET("/api/v1/auth/me");
			if (!response.data) throw new Error("Failed to fetch user");
			return response.data;
		},
		refetchOnWindowFocus: false,
		staleTime: 60000,
	});

	// Update profile mutation
	const updateMutation = useMutation({
		mutationFn: async (data: {
			first_name?: string;
			last_name?: string;
			company?: string;
			job_title?: string;
			country?: string;
		}) => {
			const response = await authClient.PATCH("/api/v1/auth/me", {
				body: data,
			});
			if (!response.data) throw new Error("Failed to update profile");
			return response.data;
		},
		onSuccess: (data) => {
			queryClient.setQueryData(["current-user"], data);
			setHasChanges(false);
			toast.success("Profile updated successfully");
		},
		onError: () => {
			toast.error("Failed to update profile");
		},
	});

	// Initialize form when user data loads
	useEffect(() => {
		if (user) {
			setFirstName(user.first_name || "");
			setLastName(user.last_name || "");
			setCompany(user.company || "");
			setJobTitle(user.job_title || "");
			setCountry(user.country || "");
		}
	}, [user]);

	const checkChanges = (fn: string, ln: string, co: string, jt: string, ct: string) => {
		setHasChanges(
			fn !== (user?.first_name || "") ||
				ln !== (user?.last_name || "") ||
				co !== (user?.company || "") ||
				jt !== (user?.job_title || "") ||
				ct !== (user?.country || "")
		);
	};

	const handleSave = () => {
		updateMutation.mutate({
			first_name: firstName || undefined,
			last_name: lastName || undefined,
			company: company || undefined,
			job_title: jobTitle || undefined,
			country: country || undefined,
		});
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<User className="h-5 w-5" animateOnHover />
					Profile Information
				</CardTitle>
				<CardDescription>Update your personal information and display name</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				{isLoading ? (
					<div className="space-y-4">
						<Skeleton className="h-4 w-24" />
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-4 w-24" />
						<Skeleton className="h-10 w-full" />
					</div>
				) : (
					<>
						<div className="space-y-2">
							<Label htmlFor={emailId}>Email</Label>
							<Input
								id={emailId}
								type="email"
								value={user?.email || ""}
								disabled
								className="bg-muted"
							/>
							<p className="text-muted-foreground text-xs">Email cannot be changed</p>
						</div>

						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div className="space-y-2">
								<Label htmlFor={firstNameId}>First Name</Label>
								<Input
									id={firstNameId}
									type="text"
									value={firstName}
									onChange={(e) => {
										setFirstName(e.target.value);
										checkChanges(e.target.value, lastName, company, jobTitle, country);
									}}
									placeholder="Enter first name"
								/>
							</div>

							<div className="space-y-2">
								<Label htmlFor={lastNameId}>Last Name</Label>
								<Input
									id={lastNameId}
									type="text"
									value={lastName}
									onChange={(e) => {
										setLastName(e.target.value);
										checkChanges(firstName, e.target.value, company, jobTitle, country);
									}}
									placeholder="Enter last name"
								/>
							</div>
						</div>

						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div className="space-y-2">
								<Label htmlFor={companyId}>Company</Label>
								<Input
									id={companyId}
									type="text"
									value={company}
									onChange={(e) => {
										setCompany(e.target.value);
										checkChanges(firstName, lastName, e.target.value, jobTitle, country);
									}}
									placeholder="Enter company name"
								/>
								{user?.company_description && (
									<p className="text-muted-foreground text-xs">{user.company_description}</p>
								)}
							</div>

							<div className="space-y-2">
								<Label htmlFor={jobTitleId}>Job Title</Label>
								<Input
									id={jobTitleId}
									type="text"
									value={jobTitle}
									onChange={(e) => {
										setJobTitle(e.target.value);
										checkChanges(firstName, lastName, company, e.target.value, country);
									}}
									placeholder="Enter job title"
								/>
							</div>
						</div>

						<CountrySelector
							value={country}
							onChange={(val) => {
								setCountry(val);
								checkChanges(firstName, lastName, company, jobTitle, val);
							}}
						/>

						{hasChanges && (
							<div className="flex gap-2">
								<Button onClick={handleSave} disabled={updateMutation.isPending}>
									{updateMutation.isPending ? "Saving..." : "Save Changes"}
								</Button>
								<Button
									variant="outline"
									onClick={() => {
										setFirstName(user?.first_name || "");
										setLastName(user?.last_name || "");
										setCompany(user?.company || "");
										setJobTitle(user?.job_title || "");
										setCountry(user?.country || "");
										setHasChanges(false);
									}}
								>
									Cancel
								</Button>
							</div>
						)}
					</>
				)}
			</CardContent>
		</Card>
	);
}

function MemoriesSection() {
	const queryClient = useQueryClient();
	const [newContent, setNewContent] = useState("");
	const [editingId, setEditingId] = useState<number | null>(null);
	const [editContent, setEditContent] = useState("");
	const [showAddForm, setShowAddForm] = useState(false);

	const { data: memories = [], isLoading } = useQuery<MemoryItem[]>({
		queryKey: ["user-memories"],
		queryFn: () => memoriesFetch(""),
	});

	const createMutation = useMutation({
		mutationFn: (content: string) =>
			memoriesFetch("", { method: "POST", body: JSON.stringify({ content }) }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["user-memories"] });
			setNewContent("");
			setShowAddForm(false);
			toast.success("Memory added");
		},
		onError: (e: Error) => toast.error(e.message),
	});

	const updateMutation = useMutation({
		mutationFn: ({ id, content }: { id: number; content: string }) =>
			memoriesFetch(`/${id}`, { method: "PATCH", body: JSON.stringify({ content }) }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["user-memories"] });
			setEditingId(null);
			toast.success("Memory updated");
		},
		onError: (e: Error) => toast.error(e.message),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => memoriesFetch(`/${id}`, { method: "DELETE" }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["user-memories"] });
			toast.success("Memory deleted");
		},
		onError: (e: Error) => toast.error(e.message),
	});

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<div>
						<CardTitle className="flex items-center gap-2">
							<Lightbulb className="h-5 w-5" animateOnHover />
							Memories
						</CardTitle>
						<CardDescription>
							Facts your teammates remember about you across conversations
						</CardDescription>
					</div>
					{!showAddForm && (
						<Button variant="outline" size="sm" onClick={() => setShowAddForm(true)}>
							<Plus className="mr-1 h-4 w-4" />
							Add
						</Button>
					)}
				</div>
			</CardHeader>
			<CardContent className="space-y-3">
				{isLoading ? (
					<div className="space-y-2">
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-10 w-full" />
					</div>
				) : (
					<>
						{showAddForm && (
							<div className="flex gap-2">
								<div className="flex-1 min-w-0">
									<Input
										value={newContent}
										onChange={(e) => setNewContent(e.target.value)}
										placeholder="Add a fact about yourself"
										maxLength={300}
										onKeyDown={(e) => {
											if (e.key === "Enter" && newContent.trim())
												createMutation.mutate(newContent.trim());
										}}
									/>
									{newContent.length >= 300 && (
										<p className="text-xs text-muted-foreground mt-1">max 300 per memory</p>
									)}
								</div>
								<Button
									size="sm"
									onClick={() => newContent.trim() && createMutation.mutate(newContent.trim())}
									disabled={createMutation.isPending || !newContent.trim()}
								>
									Save
								</Button>
								<Button
									size="sm"
									variant="outline"
									onClick={() => {
										setShowAddForm(false);
										setNewContent("");
									}}
								>
									Cancel
								</Button>
							</div>
						)}

						{memories.length === 0 && !showAddForm ? (
							<p className="text-muted-foreground text-sm py-4 text-center">
								No memories yet. Your teammates will learn about you over time, or you can add facts
								manually.
							</p>
						) : (
							memories.map((mem) => (
								<div key={mem.id} className="flex items-start gap-2 rounded-lg border p-3 min-w-0">
									{editingId === mem.id ? (
										<div className="flex flex-1 gap-2 min-w-0">
											<div className="flex-1 min-w-0">
												<Input
													value={editContent}
													onChange={(e) => setEditContent(e.target.value)}
													maxLength={300}
													onKeyDown={(e) => {
														if (e.key === "Enter" && editContent.trim())
															updateMutation.mutate({ id: mem.id, content: editContent.trim() });
														if (e.key === "Escape") setEditingId(null);
													}}
												/>
												{editContent.length >= 300 && (
													<p className="text-xs text-muted-foreground mt-1">max 300 per memory</p>
												)}
											</div>
											<Button
												size="sm"
												onClick={() =>
													editContent.trim() &&
													updateMutation.mutate({ id: mem.id, content: editContent.trim() })
												}
												disabled={updateMutation.isPending}
											>
												Save
											</Button>
											<Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
												Cancel
											</Button>
										</div>
									) : (
										<>
											<p className="flex-1 text-sm break-words min-w-0">{mem.content}</p>
											<div className="flex items-center gap-1">
												{mem.source === "agent" && (
													<Badge variant="secondary" className="text-xs gap-1">
														<Bot className="h-3 w-3" />
														agent
													</Badge>
												)}
												<Button
													variant="ghost"
													size="icon"
													className="h-7 w-7"
													onClick={() => {
														setEditingId(mem.id);
														setEditContent(mem.content);
													}}
												>
													<Pencil className="h-3.5 w-3.5" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-7 w-7 text-destructive hover:text-destructive"
													onClick={() => deleteMutation.mutate(mem.id)}
													disabled={deleteMutation.isPending}
												>
													<Trash2 className="h-3.5 w-3.5" />
												</Button>
											</div>
										</>
									)}
								</div>
							))
						)}
					</>
				)}
			</CardContent>
		</Card>
	);
}

function ToolPermissionsSection() {
	const queryClient = useQueryClient();

	const { data: policies = [], isLoading } = useQuery<ToolPermissionPolicy[]>({
		queryKey: ["tool-permissions"],
		queryFn: () => toolPermissionsFetch(""),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => toolPermissionsFetch(`/${id}`, { method: "DELETE" }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["tool-permissions"] });
			toast.success("Tool permission revoked");
		},
		onError: (e: Error) => toast.error(e.message),
	});

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<Shield className="h-5 w-5" />
					Permissions
				</CardTitle>
				<CardDescription>
					Tools you've permanently allowed. Revoking will prompt for approval again.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				{isLoading ? (
					<div className="space-y-2">
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-10 w-full" />
					</div>
				) : policies.length === 0 ? (
					<p className="text-muted-foreground text-sm py-4 text-center">
						No saved permissions yet.
					</p>
				) : (
					policies.map((p) => (
						<div key={p.id} className="flex items-center justify-between rounded-lg border p-3">
							<code className="text-sm">{p.tool_name}</code>
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7 text-destructive hover:text-destructive"
								onClick={() => deleteMutation.mutate(p.id)}
								disabled={deleteMutation.isPending}
							>
								<Trash2 className="h-3.5 w-3.5" />
							</Button>
						</div>
					))
				)}
			</CardContent>
		</Card>
	);
}

function SSOStatusSection() {
	const [showLinkDialog, setShowLinkDialog] = useState(false);

	// Fetch SSO status
	const { data: ssoStatus, isLoading } = useQuery({
		queryKey: ["sso-status"],
		queryFn: async () => {
			const response = await authClient.GET("/api/v1/auth/me/sso-status");
			if (!response.data) throw new Error("Failed to fetch SSO status");
			return response.data;
		},
	});

	const isGoogleConnected = ssoStatus?.is_google_connected || false;

	return (
		<>
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Blocks className="h-5 w-5" />
						Single Sign-On (SSO)
					</CardTitle>
					<CardDescription>Manage your Google authentication connection</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					{isLoading ? (
						<div className="space-y-2">
							<Skeleton className="h-4 w-32" />
							<Skeleton className="h-10 w-full" />
						</div>
					) : (
						<>
							<div className="flex items-center justify-between rounded-lg border p-4">
								<div className="flex items-center gap-3">
									<Blocks className="h-6 w-6 text-muted-foreground" />
									<div>
										<div className="font-medium">Google</div>
										<div className="text-muted-foreground text-sm">
											{isGoogleConnected ? "Connected to your Google account" : "Not connected"}
										</div>
									</div>
								</div>
								<Badge
									variant={isGoogleConnected ? "default" : "secondary"}
									className="flex items-center gap-1"
								>
									{isGoogleConnected ? (
										<>
											<Check className="h-3 w-3" />
											Connected
										</>
									) : (
										<>
											<X className="h-3 w-3" animateOnHover />
											Not Connected
										</>
									)}
								</Badge>
							</div>

							{!isGoogleConnected && (
								<Button onClick={() => setShowLinkDialog(true)}>Connect Google</Button>
							)}
						</>
					)}
				</CardContent>
			</Card>

			{/* Link Google Dialog */}
			<Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Connect Google Account</DialogTitle>
						<DialogDescription>
							Linking your Google account will allow you to sign in with Google in addition to your
							email and password.
						</DialogDescription>
					</DialogHeader>

					<Alert>
						<AlertDescription>
							This feature is coming soon. You'll be able to link your Google account to enable
							Google Sign-In.
						</AlertDescription>
					</Alert>

					<DialogFooter>
						<Button variant="outline" onClick={() => setShowLinkDialog(false)}>
							Close
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
function ApiKeySection() {
	const queryClient = useQueryClient();
	const [showCreatedDialog, setShowCreatedDialog] = useState(false);
	const [createdKey, setCreatedKey] = useState<string | null>(null);

	// Fetch API key info
	const { data: keyInfo, isLoading } = useQuery({
		queryKey: ["api-key"],
		queryFn: async () => {
			const response = await authClient.GET("/api/v1/auth/me/api-key");
			if (!response.data) throw new Error("Failed to fetch API key info");
			return response.data;
		},
	});

	// Generate API key mutation
	const generateMutation = useMutation({
		mutationFn: async () => {
			const response = await authClient.POST("/api/v1/auth/me/api-key", {});
			if (!response.data) throw new Error("Failed to generate API key");
			return response.data;
		},
		onSuccess: (data) => {
			setCreatedKey(data.api_key);
			setShowCreatedDialog(true);
			queryClient.invalidateQueries({ queryKey: ["api-key"] });
			toast.success("API key generated successfully");
		},
		onError: () => {
			toast.error("Failed to generate API key");
		},
	});

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
		toast.success("Copied to clipboard");
	};

	return (
		<>
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<LockKeyholeOpen className="h-5 w-5" />
						Developer API Key
					</CardTitle>
					<CardDescription>
						Use this API key with the Python SDK or REST API to build agents programmatically.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					{isLoading ? (
						<div className="space-y-2">
							<Skeleton className="h-4 w-32" />
							<Skeleton className="h-10 w-full" />
						</div>
					) : keyInfo?.has_key ? (
						<div className="space-y-4">
							<div>
								<div className="text-sm font-medium text-muted-foreground">Current API Key</div>
								<div className="mt-1">
									<code className="block rounded-md bg-muted px-3 py-2 font-mono text-sm">
										{keyInfo.prefix}••••••••••••••••••••
									</code>
								</div>
							</div>

							<Alert>
								<AlertDescription>
									This key allows external AI apps to access your connected integrations. Keep it
									secure and never share it publicly.
								</AlertDescription>
							</Alert>

							<div>
								<Button
									variant="destructive"
									onClick={() => generateMutation.mutate()}
									disabled={generateMutation.isPending}
								>
									<RefreshCw className="mr-2 h-4 w-4" animateOnHover />
									Regenerate Key
								</Button>
							</div>
						</div>
					) : (
						<div className="space-y-4">
							<Alert>
								<AlertDescription>
									You don't have an API key yet. Generate one to connect your integrations to
									external AI apps.
								</AlertDescription>
							</Alert>

							<Button
								onClick={() => generateMutation.mutate()}
								disabled={generateMutation.isPending}
							>
								<LockKeyholeOpen className="mr-2 h-4 w-4" />
								Generate API Key
							</Button>
						</div>
					)}
				</CardContent>
			</Card>

			{/* API Key Created Dialog */}
			<Dialog open={showCreatedDialog} onOpenChange={setShowCreatedDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>API Key Created</DialogTitle>
						<DialogDescription>
							Save this API key now. You won't be able to see it again!
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4">
						<div>
							<div className="text-sm font-medium">Your API Key</div>
							<div className="mt-1 flex items-center gap-2">
								<code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm break-all">
									{createdKey}
								</code>
								<Tooltip delayDuration={300}>
									<TooltipTrigger asChild>
										<CopyButton
											content={createdKey || ""}
											variant="outline"
											size="sm"
											aria-label="Copy API key"
										/>
									</TooltipTrigger>
									<TooltipContent variant="subtle">Copy API key</TooltipContent>
								</Tooltip>
							</div>
						</div>

						<Alert>
							<AlertDescription className="text-sm">
								⚠️ Make sure to copy your API key now. You won't be able to see it again!
							</AlertDescription>
						</Alert>
					</div>

					<DialogFooter>
						<Button
							onClick={() => {
								if (createdKey) {
									copyToClipboard(createdKey);
								}
								setShowCreatedDialog(false);
								setCreatedKey(null);
							}}
						>
							Copy and Close
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
